/* Copyright (c) 2024 MOCA Systems, Inc.  All rights reserved. */

import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MultiPanelModule } from 'src/app/components/multi-panel/multi-panel.module';
import { TicketListPanelComponent } from 'src/app/components/ticket/ticket-list-panel/ticket-list-panel.component';
import * as strings from '@touchplan/strings';
import { MaterialModule } from 'src/app/common/material/material-module';
import { SharedUIMaterialModule } from 'src/app/shared/ui/material/shared-ui-material.module';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, of } from 'rxjs';
import { Ticket } from '@touchplan/common';
import { debounceTime, distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import { UserPreferenceStateService } from 'src/app/services/user-preference-state.service';
import { MatSliderModule } from '@angular/material/slider';
import { subjectSubscribeWithoutComplete } from 'src/app/plain-javascript/services/rxjs-internal-extensions';

export enum SortTicketListOptions {
	Ascending = 'asc',
	Descending = 'dec',
}

const DEFAULT_TRANSPARENCY = 0;
@Component({
	selector: 'tp-filtered-ticket-list-panel',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		MultiPanelModule,
		TicketListPanelComponent,
		MaterialModule,
		SharedUIMaterialModule,
		MatSliderModule,
	],
	templateUrl: './filtered-ticket-list-panel.component.html',
	styleUrls: ['./filtered-ticket-list-panel.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilteredTicketListPanelComponent implements OnDestroy, OnInit {
	private userPreferences = inject(UserPreferenceStateService);
	protected ticketList$: BehaviorSubject<Map<string, Ticket>> = new BehaviorSubject<Map<string, Ticket>>(
		new Map()
	);
	private _previousInputTicketSubscription: Subscription | undefined;
	private _destroy$ = new Subject<void>();

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-3856
	// "this works", but the boilerplate is tedious (see ticket)
	@Input() set tickets(ticket$: Observable<Map<string, Ticket>>) {
		if (this._previousInputTicketSubscription) {
			this._previousInputTicketSubscription.unsubscribe();
			delete this._previousInputTicketSubscription;
		}
		ticket$.pipe(takeUntil(this._destroy$)).subscribe(subjectSubscribeWithoutComplete(this.ticketList$));
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-3856
	@Output() public ticketClick: EventEmitter<Ticket> = new EventEmitter<Ticket>();
	@Output() public transparencyChange: EventEmitter<number> = new EventEmitter<number>();

	public transparency = new FormControl(DEFAULT_TRANSPARENCY);

	protected appStrings = strings;
	public isTicketListPanelCollapsed: boolean;
	public sortTicketsControl = new FormControl(SortTicketListOptions.Ascending);
	public filteredSortedTickets$: Observable<Map<string, Ticket>> = combineLatest([
		this.ticketList$,
		this.sortTicketsControl.valueChanges.pipe(startWith(SortTicketListOptions.Ascending)),
	]).pipe(
		map(([tickets, sortOption]) => {
			const sortedTickets = new Map(
				[...tickets.entries()].sort((a, b) => {
					if (sortOption === SortTicketListOptions.Ascending) {
						return a[1].view.liveStart < b[1].view.liveStart ? -1 : 1;
					} else {
						return b[1].view.liveStart > a[1].view.liveStart ? 1 : -1;
					}
				})
			);
			return sortedTickets;
		})
	);

	public sortTicketListOptions = new Map<string, string>([
		[strings.PLAN_FILTER_TICKET_LIST_SORT_BY_START_DATE_ASC, SortTicketListOptions.Ascending],
		[strings.PLAN_FILTER_TICKET_LIST_SORT_BY_START_DATE_DESC, SortTicketListOptions.Descending],
	]);

	public ngOnInit() {
		this.listenPreferencesChanges();
		this.updateFormControlsOnPreferencesChanges();
	}

	public ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	private listenPreferencesChanges() {
		this.sortTicketsControl.valueChanges
			.pipe(distinctUntilChanged(), takeUntil(this._destroy$))
			.subscribe((sortBy) => {
				this.userPreferences.planFilterSortBy = sortBy;
			});
		this.transparency.valueChanges
			.pipe(debounceTime(250), distinctUntilChanged(), takeUntil(this._destroy$))
			.subscribe((value) => {
				this.userPreferences.planFilterTransparency = value;
				this.emitTransparencyChange(value);
			});
	}

	private updateFormControlsOnPreferencesChanges() {
		this.userPreferences.planFilterSortBy$.pipe(takeUntil(this._destroy$)).subscribe((sortBy) => {
			this.sortTicketsControl.setValue(sortBy, { emitEvent: false });
		});
		this.userPreferences.planFilterTransparency$.pipe(takeUntil(this._destroy$)).subscribe((transparency) => {
			this.transparency.setValue(transparency, { emitEvent: false });
			this.emitTransparencyChange(transparency);
		});
	}

	public onFocusOnTicket(ticket: Ticket) {
		this.ticketClick.emit(ticket);
	}

	private emitTransparencyChange(value: number): void {
		// Convert the input percentage (0-100) to a decimal value (0-1).
		const decimalAlpha = value / 100;

		/*
		 * Calculate the alpha value to emit, ensuring it falls between 0 and 0.5.
		 * If the decimalAlpha is greater than 0, the resulting alpha is calculated as (1 - decimalAlpha) / 2.
		 * If the transparency value is 0, the alpha defaults to 0.5 for maximum opacity.
		 */
		const alpha = decimalAlpha > 0 ? (1 - decimalAlpha) / 2 : 0.5;

		// Emit the computed alpha value.
		this.transparencyChange.emit(alpha);
	}
}
